import React, { useEffect, useState } from 'react'
import { Stack } from '@sureco/design-system'
import { Modal, Table, Tag, Tooltip, Typography, message } from 'antd'
import dayjs from 'dayjs'
import styled from 'styled-components'
import ClientSelect from 'components/ClientSelect'
import Layout from 'enrollment-platform/pages/HRIS/pages/ApiVisibility/components/Layout'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { CENSUS_MANAGEMENT_PATH } from '../constants'
import {
  AutoTermReportRecord,
  CensusIntegrationUploadDto,
  getCensusIntegrationUploadDownloads,
  getCensusIntegrationUploads,
} from '../api/census-integration-uploads-api-client'

const { Link } = Typography

const FitTable = styled.div`
  width: 100%;

  .ant-table-wrapper,
  .ant-table,
  .ant-table-container,
  .ant-table-content,
  .ant-table-body,
  table {
    width: 100% !important;
    max-width: 100%;
  }

  .ant-table-content,
  .ant-table-body,
  .ant-table-container {
    overflow-x: hidden !important;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 8px 4px;
    font-size: 12px;
  }

  .ant-table-cell-ellipsis {
    max-width: 0;
  }
`

const clipStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

function Clip({
  text,
  children,
}: {
  text: string
  children?: React.ReactNode
}) {
  return (
    <Tooltip title={text} placement="topLeft">
      <span style={clipStyle}>{children ?? text}</span>
    </Tooltip>
  )
}

const count = (value: number | null) => (value == null ? '—' : value)

const seconds = (ms: number) => `${(ms / 1000).toFixed(1)}s`

function IntegrationsUploads() {
  const { clients } = useCensusV2Clients()
  const [clientPublicKey, setClientPublicKey] = useState('')
  const [rows, setRows] = useState<CensusIntegrationUploadDto[]>([])
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<{
    title: string
    records: AutoTermReportRecord[]
  } | null>(null)

  useEffect(() => {
    let ignore = false

    const load = async () => {
      setLoading(true)
      try {
        const nextRows = await getCensusIntegrationUploads(
          clientPublicKey || undefined
        )
        if (ignore) return
        setRows(nextRows)
      } catch (error) {
        if (ignore) return
        setRows([])
        message.error(
          `Failed to load integrations uploads: ${(error as Error).message}`
        )
      } finally {
        if (!ignore) setLoading(false)
      }
    }

    load()
    return () => {
      ignore = true
    }
  }, [clientPublicKey])

  const download = async (
    id: string,
    which: 'flexspringUrl' | 'transformedUrl'
  ) => {
    // Reserve the tab inside the click. Opening it after the signed URL
    // returns is outside the user gesture, and browsers block that popup.
    const tab = window.open('about:blank', '_blank')
    if (!tab) {
      message.error(
        'Download blocked. Allow popups for this site and try again.'
      )
      return
    }
    tab.opener = null

    try {
      const urls = await getCensusIntegrationUploadDownloads(id)
      const url = urls[which]
      if (!url) {
        tab.close()
        message.info('File is not available')
        return
      }
      if (!tab.closed) {
        tab.location.replace(url)
      }
    } catch (error) {
      if (!tab.closed) tab.close()
      message.error(`Failed to download file: ${(error as Error).message}`)
    }
  }

  return (
    <Layout title="CMv2 API Visibility" maxWidth="100%">
      <Stack direction="col" space={16}>
        <ClientSelect
          companies={clients.map((client) => ({
            id: client.id,
            companyName: client.name,
          }))}
          selectedCompanyId={clientPublicKey}
          allCompaniesPlacholder={{ id: '', companyName: 'All companies' }}
          onChange={setClientPublicKey}
          style={{ width: 320 }}
        />
        <FitTable>
          <Table
            rowKey="id"
            loading={loading}
            dataSource={rows}
            size="small"
            tableLayout="fixed"
            pagination={{ pageSize: 20 }}
            columns={[
              {
                title: 'When',
                dataIndex: 'createdAt',
                width: '6%',
                ellipsis: { showTitle: false },
                render: (value: string) => {
                  const text = dayjs(value).format('MM/DD/YYYY hh:mm A')
                  return <Clip text={text} />
                },
              },
              {
                title: 'Company',
                dataIndex: 'companyName',
                width: '6%',
                ellipsis: { showTitle: false },
                render: (
                  value: string | null,
                  row: CensusIntegrationUploadDto
                ) => <Clip text={value || row.clientPublicKey} />,
              },
              {
                title: 'File',
                dataIndex: 'fileName',
                width: '7%',
                ellipsis: { showTitle: false },
                render: (
                  value: string | null,
                  row: CensusIntegrationUploadDto
                ) => {
                  const text = value || row.importId || '—'
                  return row.importId ? (
                    <Clip text={text}>
                      <Link
                        href={`${CENSUS_MANAGEMENT_PATH}/${row.importId}/DRAFT_EMPLOYEE`}
                      >
                        {text}
                      </Link>
                    </Clip>
                  ) : (
                    <Clip text={text} />
                  )
                },
              },
              {
                title: 'File status',
                dataIndex: 'censusStatus',
                width: '5%',
                ellipsis: { showTitle: false },
                render: (value: string, row: CensusIntegrationUploadDto) => (
                  <Clip
                    text={
                      row.censusError ? `${value}: ${row.censusError}` : value
                    }
                  >
                    <Tag color={value === 'success' ? 'green' : 'red'}>
                      {value}
                    </Tag>
                    {row.censusError ? ` ${row.censusError}` : null}
                  </Clip>
                ),
              },
              {
                title: 'File time',
                dataIndex: 'censusProcessedMs',
                width: '5%',
                ellipsis: { showTitle: false },
                render: (value: number) => <Clip text={seconds(value)} />,
              },
              {
                title: 'Total',
                dataIndex: 'totalRecords',
                width: '4%',
                ellipsis: { showTitle: false },
                render: count,
              },
              {
                title: 'Eligible',
                dataIndex: 'eligible',
                width: '4%',
                ellipsis: { showTitle: false },
                render: count,
              },
              {
                title: 'Ineligible',
                dataIndex: 'ineligible',
                width: '5%',
                ellipsis: { showTitle: false },
                render: count,
              },
              {
                title: 'Duplicate',
                dataIndex: 'duplicateRecords',
                width: '5%',
                ellipsis: { showTitle: false },
                render: count,
              },
              {
                title: 'New',
                dataIndex: 'newRecords',
                width: '4%',
                ellipsis: { showTitle: false },
                render: count,
              },
              {
                title: 'Update',
                dataIndex: 'updateRecords',
                width: '4%',
                ellipsis: { showTitle: false },
                render: count,
              },
              {
                title: 'Error',
                dataIndex: 'errorRecords',
                width: '4%',
                ellipsis: { showTitle: false },
                render: count,
              },
              {
                title: 'Auto-term',
                dataIndex: 'autoTermStatus',
                width: '5%',
                ellipsis: { showTitle: false },
                render: (value: string, row: CensusIntegrationUploadDto) => (
                  <Clip
                    text={
                      row.autoTermError
                        ? `${value}: ${row.autoTermError}`
                        : value
                    }
                  >
                    <Tag>{value}</Tag>
                    {row.autoTermError ? ` ${row.autoTermError}` : null}
                  </Clip>
                ),
              },
              {
                title: 'Auto-term time',
                dataIndex: 'autoTermProcessedMs',
                width: '5%',
                ellipsis: { showTitle: false },
                render: (value: number) => <Clip text={seconds(value)} />,
              },
              {
                title: 'Already terminated',
                dataIndex: 'alreadyTerminated',
                width: '6%',
                ellipsis: { showTitle: false },
                render: (value: number, row: CensusIntegrationUploadDto) =>
                  value > 0 ? (
                    <Link
                      role="button"
                      aria-label={`Show ${value} already terminated`}
                      onClick={() =>
                        setReport({
                          title: 'Already terminated',
                          records: row.alreadyTerminatedRecords,
                        })
                      }
                    >
                      {value}
                    </Link>
                  ) : (
                    count(value)
                  ),
              },
              {
                title: 'New enqueued',
                dataIndex: 'newEnqueued',
                width: '5%',
                ellipsis: { showTitle: false },
              },
              {
                title: 'Missing termination date',
                dataIndex: 'missingTerminationDate',
                width: '7%',
                ellipsis: { showTitle: false },
                render: (value: number, row: CensusIntegrationUploadDto) =>
                  value > 0 ? (
                    <Link
                      role="button"
                      aria-label={`Show ${value} missing termination dates`}
                      onClick={() =>
                        setReport({
                          title: 'Missing termination date',
                          records: row.missingTerminationDateRecords,
                        })
                      }
                    >
                      {value}
                    </Link>
                  ) : (
                    count(value)
                  ),
              },
              {
                title: 'Ambiguous',
                dataIndex: 'ambiguousMemberMatch',
                width: '5%',
                ellipsis: { showTitle: false },
                render: (value: number, row: CensusIntegrationUploadDto) =>
                  value > 0 ? (
                    <Link
                      role="button"
                      aria-label={`Show ${value} ambiguous member matches`}
                      onClick={() =>
                        setReport({
                          title: 'Ambiguous member matches',
                          records: row.ambiguousMemberRecords,
                        })
                      }
                    >
                      {value}
                    </Link>
                  ) : (
                    count(value)
                  ),
              },
              {
                title: 'Downloads',
                width: '8%',
                render: (_: unknown, row: CensusIntegrationUploadDto) => (
                  <Stack direction="col" space={4}>
                    <Clip text="FlexSpring">
                      <Link
                        role="button"
                        onClick={() => download(row.id, 'flexspringUrl')}
                      >
                        FlexSpring
                      </Link>
                    </Clip>
                    <Clip text="Transformed">
                      <Link
                        role="button"
                        onClick={() => download(row.id, 'transformedUrl')}
                      >
                        Transformed
                      </Link>
                    </Clip>
                  </Stack>
                ),
              },
            ]}
          />
        </FitTable>
        <Modal
          title={report?.title}
          open={report != null}
          onCancel={() => setReport(null)}
          footer={null}
          width={960}
        >
          <Table<AutoTermReportRecord & { key: number }>
            size="small"
            tableLayout="fixed"
            pagination={false}
            dataSource={(report?.records ?? []).map((record, index) => ({
              ...record,
              key: index,
            }))}
            rowKey="key"
            columns={[
              {
                title: 'Name',
                render: (_: unknown, record: AutoTermReportRecord) =>
                  [record.firstName, record.lastName]
                    .filter(Boolean)
                    .join(' ') || '—',
              },
              {
                title: 'File employee id',
                dataIndex: 'fileCompanyEmployeeId',
                render: (value: string) => value || '—',
              },
              {
                title: 'File status',
                dataIndex: 'fileEmploymentStatus',
                render: (value: string) => value || '—',
              },
              {
                title: 'Termination date',
                dataIndex: 'terminationDate',
                render: (value: string) => value || '—',
              },
              {
                title: 'SSN',
                dataIndex: 'ssn',
                render: (value: string) => value || '—',
              },
              {
                title: 'Employee public key',
                dataIndex: 'employeePublicKey',
                render: (value: string) => value || '—',
              },
              {
                title: 'Member employee id',
                dataIndex: 'memberCompanyEmployeeId',
                render: (value: string) => value || '—',
              },
              {
                title: 'Member status',
                dataIndex: 'memberEmploymentStatus',
                render: (value: string) => value || '—',
              },
            ]}
          />
        </Modal>
      </Stack>
    </Layout>
  )
}

export default IntegrationsUploads
